import React, { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/custom-radix-components/accordion";
import { generateFaqSchema } from "~/lib/helpers/schemaHelper";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "~/components/ui/dialog";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import EnquireNowForm from "../common/EnquireNowForm";
import { DEFAULT_COUNTRY } from "~/lib/constants/generalConstants";
import { useLocation } from "@remix-run/react";
import Cookies from "js-cookie";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/custom-bottom-drawer";
import { RegionConfig, useRegionConfig } from "~/store/regionConfigStore";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQsProps {
  faqs: FAQItem[] | { data?: FAQItem[]; faqs?: FAQItem[] } | null | undefined;
  title?: string; // Made optional as we are hardcoding the title in the new design
  pageUrl?: string;
  contactText?: string;
  buttonText?: string;
}

function FAQs({
  faqs,
  title,
  pageUrl,
  contactText = "Contact us if you still have any questions to make your experience smoother.",
  buttonText = "ENQUIRE NOW",
}: FAQsProps) {
  const isClient = typeof window !== "undefined";

  const screenWidth = isClient
    ? parseInt(Cookies.get("screenWidth") || "0", 10)
    : 1024;
  const isMobile = screenWidth > 0 && screenWidth < 768;

  const [isEnquireNowOpen, setIsEnquireNowOpen] = useState(false);
  const toggleEnquireModal = (open: boolean) => {
    setIsEnquireNowOpen(open);
  };

  let normalizedPageUrl = "";
  try {
    if (typeof pageUrl === "string" && pageUrl) {
      const u = new URL(pageUrl);
      normalizedPageUrl = `${u.origin}${u.pathname}`.replace(/\/$/, "");
    }
  } catch {
    normalizedPageUrl = "";
  }

  const webpageId = normalizedPageUrl
    ? `${normalizedPageUrl}#webpage`
    : undefined;
  const faqId = normalizedPageUrl ? `${normalizedPageUrl}#faq` : undefined;

  const safeFaqs = Array.isArray(faqs)
    ? faqs
    : (faqs as { data?: FAQItem[]; faqs?: FAQItem[] } | null | undefined)
      ?.data ||
    (faqs as { data?: FAQItem[]; faqs?: FAQItem[] } | null | undefined)
      ?.faqs ||
    [];

  const { regionConfig } = useRegionConfig();
  const location = useLocation();
  let { pathname } = location;

  const schema = generateFaqSchema(safeFaqs, {
    pageUrl: normalizedPageUrl || undefined,
    webpageId,
    faqId,
  });

  return (
    <>
      {safeFaqs.length > 0 && schema != null && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
      {safeFaqs.length > 0 && (
        <section
          id={PAGE_SECTION_IDS.faqs}
          className={`w-full mx-auto py-12 md:py-24 ${PAGE_SECTION_SCROLL_CLASS}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-12 items-start">
            {/* Left Column: Content and Button */}
            <div className="flex flex-col gap-6 mx-8 md:mx">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
                Frequently <span className="text-oh-primary">Asked</span> <br className="hidden md:block" /> Questions
              </h2>
              <p className="text-muted-foreground text-lg max-w-md">
                {contactText}
              </p>
              <div>
                {/* Enquire Now Button */}
                {isMobile ? (
                  <Drawer
                    open={isEnquireNowOpen}
                    onOpenChange={toggleEnquireModal}
                  >
                    <OhButton
                      className="w-full md:w-auto rounded-xl h-12"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEnquireNowOpen(true);
                      }}
                    >
                      Enquire Now
                    </OhButton>

                    <DrawerContent>
                      <DrawerHeader className='sticky top-0 z-10 border-b bg-background px-4 py-2 text-oh-black text-base font-medium'>
                        <div className='flex items-center justify-between'>
                          <DrawerTitle>Start Your Office Search</DrawerTitle>
                        </div>
                      </DrawerHeader>

                      <EnquireNowForm
                        canonicalCountry={regionConfig?.country || DEFAULT_COUNTRY}
                        region={regionConfig?.region}
                        path={pathname}
                        className="w-full"
                      />
                    </DrawerContent>
                  </Drawer>
                ) : (
                  <Dialog>
                    <DialogTrigger asChild>
                      <OhButton
                        className="w-full md:w-auto rounded-xl h-12"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Enquire Now
                      </OhButton>
                    </DialogTrigger>

                    <DialogContent
                      className="sm:max-w-xl w-full max-h-[85vh] overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DialogHeader className="font-semibold border-b pb-4">
                        Start Your Office Search
                      </DialogHeader>

                      <EnquireNowForm
                        canonicalCountry={regionConfig?.country || DEFAULT_COUNTRY}
                        region={regionConfig?.region}
                        path={pathname}
                        className="w-full"
                      />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>

            {/* Right Column: Accordion */}
            <div className="mx-8 md:mx-0">
              <Accordion
                type="multiple"
                className="w-full border-t"
              >
                {safeFaqs.map((faq: FAQItem, i: number) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-gray-200">
                    <AccordionTrigger className="group text-left font-medium text-base py-6 hover:no-underline [&>svg]:hidden flex justify-between items-center w-full">
                      <span className="max-w-80 md:max-w-none">{faq.question}</span>
                      <span className="transition-all duration-200 text-black group-data-[state=open]:rotate-45 group-data-[state=open]:text-oh-primary">
                        <Plus className="h-5 w-5 shrink-0" />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent
                      forceMount
                      className="pb-6 text-base text-muted-foreground data-[state=closed]:hidden pr-12"
                    >
                      <div dangerouslySetInnerHTML={{ __html: faq.answer }}></div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section >
      )
      }
    </>
  );
}

export default FAQs;